import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, ListChecks, MessageSquare, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, cleanParams, formatErr } from "@/lib/api";
import { PRIORITIES, PRIORITY_STYLE, TASK_STATUSES, can, isManager } from "@/lib/constants";
import { fmtDateOnly, fmtDateTime, isOverdueYMD } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useAssignableOptions, useUserMap } from "@/hooks/useData";
import { Chip, EmptyState, InfoRow, NSelect, PageHeader, Spinner, UserAvatar } from "@/components/common";
import { ConfirmDialog } from "@/components/Dialogs";
import TaskDialog from "@/components/tasks/TaskDialog";
import { cn } from "@/lib/utils";

const PBadge = ({ p }) => <span className={cn("rounded-md border px-2 py-0.5 text-[11px] font-semibold", PRIORITY_STYLE[p])}>{p}</span>;
const late = (t) => t.status !== "Done" && isOverdueYMD(t.due_date);

async function patchTask(qc, id, body) {
  try {
    await api.patch(`/tasks/${id}`, body);
    qc.invalidateQueries({ queryKey: ["tasks"] });
    qc.invalidateQueries({ queryKey: ["task", id] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  } catch (e) {
    toast.error(formatErr(e));
  }
}

function TaskSheet({ id, onClose, onEdit }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const userMap = useUserMap();
  const [text, setText] = useState("");
  const [del, setDel] = useState(false);
  const { data: t } = useQuery({ queryKey: ["task", id], queryFn: async () => (await api.get(`/tasks/${id}`)).data, enabled: !!id });
  const comment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/tasks/${id}/comments`, { text });
      setText("");
      qc.invalidateQueries({ queryKey: ["task", id] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    } catch (ex) {
      toast.error(formatErr(ex));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/tasks/${id}`);
      qc.invalidateQueries({ queryKey: ["tasks"] });
      onClose();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="task-sheet">
        {!t ? <Spinner /> : (
          <div className="space-y-5">
            <SheetHeader><SheetTitle className={cn("text-left", late(t) && "text-destructive")} data-testid="task-sheet-title">{t.title}</SheetTitle></SheetHeader>
            <div className="flex flex-wrap gap-2"><PBadge p={t.priority} /><span className="rounded-md bg-secondary px-2 py-0.5 text-[11px] font-semibold">{t.source}</span>{late(t) && <span className="text-xs font-semibold text-destructive">Overdue</span>}</div>
            {t.description && <p className="whitespace-pre-wrap text-sm">{t.description}</p>}
            <NSelect testid="task-sheet-status" value={t.status} onChange={(v) => patchTask(qc, id, { status: v })} options={TASK_STATUSES} />
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="Assigned to" value={userMap[t.assigned_to]?.name} />
              <InfoRow label="Assigned by" value={userMap[t.assigned_by]?.name} />
              <InfoRow label="Due" value={fmtDateOnly(t.due_date)} />
              <InfoRow label="Created" value={fmtDateTime(t.created_at)} />
              {t.lead_id && <InfoRow label="Lead" value={<Link className="text-primary underline" to={`/leads/${t.lead_id}`}>Open lead</Link>} />}
              {t.property_id && <InfoRow label="Property" value={<Link className="text-primary underline" to={`/properties/${t.property_id}`}>Open property</Link>} />}
              {t.source_message_id && can(user, "chat") && <InfoRow label="Source" value={<Link className="text-primary underline" to={`/chat?m=${t.source_message_id}`}>Chat message</Link>} />}
            </div>
            {isManager(user) && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => onEdit(t)} data-testid="task-sheet-edit"><Pencil className="mr-1.5 h-4 w-4" />Edit</Button>
                <Button variant="outline" size="sm" className="text-destructive" onClick={() => setDel(true)} data-testid="task-sheet-delete"><Trash2 className="h-4 w-4" /></Button>
              </div>
            )}
            <div>
              <p className="label-xs mb-2">Comments</p>
              <div className="space-y-3">
                {t.comments.map((c) => (
                  <div key={c.id} className="rounded-lg bg-secondary/60 p-3 text-sm" data-testid={`task-comment-${c.id}`}>
                    <p>{c.text}</p><p className="mt-1 text-[11px] text-muted-foreground">{c.user_name} · {fmtDateTime(c.at)}</p>
                  </div>
                ))}
              </div>
              <form onSubmit={comment} className="mt-3 space-y-2">
                <Textarea data-testid="task-comment-input" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Add a comment" />
                <Button size="sm" disabled={!text.trim()} data-testid="task-comment-submit">Comment</Button>
              </form>
            </div>
          </div>
        )}
        <ConfirmDialog open={del} onOpenChange={setDel} title="Delete this task?" text="This cannot be undone." onConfirm={remove} testid="task-delete-dialog" />
      </SheetContent>
    </Sheet>
  );
}

export default function TasksPage() {
  const { user } = useAuth();
  const mgr = isManager(user);
  const qc = useQueryClient();
  const userMap = useUserMap();
  const assignOpts = useAssignableOptions();
  const [params, setParams] = useSearchParams();
  const [dlg, setDlg] = useState({ open: false, task: null });
  const f = useMemo(() => Object.fromEntries(["scope", "assigned_to", "status", "due", "priority"].map((k) => [k, params.get(k) || ""])), [params]);
  const scope = mgr ? f.scope || "mine" : "mine";
  const set = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };
  const apiParams = cleanParams({ ...f, scope, open: undefined });
  const { data: tasks = [], isLoading } = useQuery({ queryKey: ["tasks", apiParams], queryFn: async () => (await api.get("/tasks", { params: apiParams })).data });

  return (
    <>
      <PageHeader title={scope === "mine" ? "My Tasks" : "All Tasks"} testid="tasks-title" subtitle={`${tasks.length} tasks`}
        actions={mgr && <Button size="sm" onClick={() => setDlg({ open: true, task: null })} data-testid="tasks-add-button"><Plus className="mr-1.5 h-4 w-4" />New task</Button>} />
      <div className="mb-4 flex gap-2 overflow-x-auto scrollbar-none">
        {mgr && <><Chip testid="tasks-scope-mine" active={scope === "mine"} onClick={() => set("scope", "mine")}>My Tasks</Chip><Chip testid="tasks-scope-all" active={scope === "all"} onClick={() => set("scope", "all")}>All Tasks</Chip><span className="mx-1 h-5 w-px self-center bg-border" /></>}
        {[["", "Any due date"], ["overdue", "Overdue"], ["today", "Due today"], ["week", "Next 7 days"]].map(([k, l]) => <Chip key={k} testid={`tasks-due-${k || "any"}`} active={f.due === k} onClick={() => set("due", k)}>{l}</Chip>)}
      </div>
      <div className="mb-6 grid grid-cols-2 gap-2 md:grid-cols-4">
        <NSelect testid="tasks-status-filter" value={f.status} onChange={(v) => set("status", v)} placeholder="All statuses" options={TASK_STATUSES} />
        <NSelect testid="tasks-priority-filter" value={f.priority} onChange={(v) => set("priority", v)} placeholder="All priorities" options={PRIORITIES} />
        {mgr && scope === "all" && <NSelect testid="tasks-person-filter" value={f.assigned_to} onChange={(v) => set("assigned_to", v)} placeholder="Everyone" options={assignOpts} />}
      </div>
      {isLoading ? <Spinner /> : !tasks.length ? <EmptyState icon={ListChecks} testid="tasks-empty" title="No tasks here" /> : (
        <div className="space-y-2" data-testid="tasks-list">
          {tasks.map((t) => (
            <div key={t.id} data-testid={`task-row-${t.id}`} onClick={() => set("open", t.id)}
              className={cn("flex cursor-pointer items-center gap-3 rounded-xl border bg-card p-3 transition-colors hover:border-primary/40", late(t) && "border-l-4 border-l-destructive bg-red-50/40 dark:bg-red-950/20")}>
              <div className="min-w-0 flex-1">
                <p className={cn("truncate text-sm font-semibold", t.status === "Done" && "text-muted-foreground line-through")}>{t.title}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <PBadge p={t.priority} />
                  {t.due_date && <span className={cn("flex items-center gap-1", late(t) && "font-semibold text-destructive")} data-testid={late(t) ? "task-overdue" : undefined}><CalendarDays className="h-3.5 w-3.5" />{fmtDateOnly(t.due_date)}</span>}
                  {t.source !== "Manual" && <span className="rounded bg-secondary px-1.5">{t.source}</span>}
                  {t.comments?.length > 0 && <span className="flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />{t.comments.length}</span>}
                </div>
              </div>
              <UserAvatar user={userMap[t.assigned_to]} />
              <select onClick={(e) => e.stopPropagation()} value={t.status} onChange={(e) => patchTask(qc, t.id, { status: e.target.value })} data-testid={`task-status-${t.id}`} className="h-8 rounded-md border bg-background px-2 text-xs">
                {TASK_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
      <TaskSheet id={params.get("open")} onClose={() => set("open", "")} onEdit={(t) => setDlg({ open: true, task: t })} />
      <TaskDialog open={dlg.open} task={dlg.task} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} onSaved={(t) => qc.invalidateQueries({ queryKey: ["task", t.id] })} />
    </>
  );
}
